// ---------------------------------------------------------------------------
// C2+C3: Dynamic Content Variant System — Types + 8 Rule Types
// ---------------------------------------------------------------------------
// Per-component variant system stored IN Puck JSON props.
// Each component can have a `variants` array. Each variant has:
//   - A rule (condition that triggers it)
//   - A badge label (for UI display)
//   - Prop overrides (merged with default props when active)
//
// Resolution: first matching rule wins. Default if no match.
// 8 rule types: utm_campaign, utm_source, referral, returning_visitor,
// device, geo_language, time_of_day, contact_status.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Rule Types
// ---------------------------------------------------------------------------

export const VARIANT_RULE_TYPES = [
  "utm_campaign",       // 🏷️ Match URL param utm_campaign
  "utm_source",         // 📢 Match URL param utm_source
  "referral",           // 🔗 Match HTTP Referer header domain
  "returning_visitor",  // 🔄 Match cookie for returning visits
  "device",             // 📱/💻 Match User-Agent (mobile/desktop/tablet)
  "geo_language",       // 🌍 Match Accept-Language or CF geo header
  "time_of_day",        // 🕐 Match server time (morning/afternoon/evening/night)
  "contact_status",     // 👤 Match known/anonymous from cookie or auth
] as const;

export type VariantRuleType = (typeof VARIANT_RULE_TYPES)[number];

export const RULE_BADGES: Record<VariantRuleType, string> = {
  utm_campaign: "🏷️",
  utm_source: "📢",
  referral: "🔗",
  returning_visitor: "🔄",
  device: "📱",
  geo_language: "🌍",
  time_of_day: "🕐",
  contact_status: "👤",
};

// ---------------------------------------------------------------------------
// Variant Rule
// ---------------------------------------------------------------------------

export interface VariantRule {
  type: VariantRuleType;
  /** Value to match against. Interpretation depends on type. */
  value: string;
  /** Optional: operator for flexible matching */
  operator?: "equals" | "contains" | "starts_with" | "regex";
}

// ---------------------------------------------------------------------------
// Component Variant
// ---------------------------------------------------------------------------

export interface ComponentVariant {
  /** Unique variant ID */
  id: string;
  /** Display label for carousel badge */
  label: string;
  /** Rule that triggers this variant */
  rule: VariantRule;
  /** Badge emoji based on rule type */
  badge: string;
  /** Props to override when this variant is active (merged with defaults) */
  overrides: Record<string, unknown>;
  /** Priority: lower number = higher priority. First match wins. */
  priority: number;
}

// ---------------------------------------------------------------------------
// Visitor Context (extracted from request at serve time)
// ---------------------------------------------------------------------------

export interface VisitorContext {
  utmCampaign?: string;
  utmSource?: string;
  utmMedium?: string;
  referrer?: string;
  /** Referrer domain (extracted from full referrer URL) */
  referrerDomain?: string;
  isReturningVisitor: boolean;
  /** "mobile" | "desktop" | "tablet" */
  device: string;
  /** ISO country code from CF-IPCountry or geo lookup */
  country?: string;
  /** Primary language from Accept-Language */
  language?: string;
  /** "morning" | "afternoon" | "evening" | "night" */
  timeOfDay: string;
  /** "known" (has auth/cookie) | "anonymous" */
  contactStatus: string;
}

// ---------------------------------------------------------------------------
// Rule Evaluation
// ---------------------------------------------------------------------------

/**
 * Evaluate a single variant rule against visitor context.
 * Returns true if the rule matches.
 */
export function evaluateRule(rule: VariantRule, ctx: VisitorContext): boolean {
  const value = rule.value.toLowerCase();
  const op = rule.operator ?? "equals";

  switch (rule.type) {
    case "utm_campaign":
      return matchString(ctx.utmCampaign, value, op);
    case "utm_source":
      return matchString(ctx.utmSource, value, op);
    case "referral":
      return matchString(ctx.referrerDomain, value, op);
    case "returning_visitor":
      return value === "true" ? ctx.isReturningVisitor : !ctx.isReturningVisitor;
    case "device":
      return matchString(ctx.device, value, op);
    case "geo_language": {
      // Match country OR language (e.g., "FR" matches country=FR or language=fr)
      const countryMatch = matchString(ctx.country, value, op);
      const langMatch = matchString(ctx.language, value, op);
      return countryMatch || langMatch;
    }
    case "time_of_day":
      return matchString(ctx.timeOfDay, value, op);
    case "contact_status":
      return matchString(ctx.contactStatus, value, op);
    default:
      return false;
  }
}

function matchString(actual: string | undefined, expected: string, op: string): boolean {
  if (!actual) return false;
  const a = actual.toLowerCase();
  switch (op) {
    case "equals": return a === expected;
    case "contains": return a.includes(expected);
    case "starts_with": return a.startsWith(expected);
    case "regex": {
      try { return new RegExp(expected, "i").test(a); }
      catch { return false; }
    }
    default: return a === expected;
  }
}

// ---------------------------------------------------------------------------
// Variant Resolution
// ---------------------------------------------------------------------------

/**
 * Resolve which variant should be active for a component.
 * Evaluates all variants in priority order. First match wins.
 * Returns the variant's prop overrides, or null for default.
 */
export function resolveVariant(
  variants: ComponentVariant[],
  ctx: VisitorContext,
): ComponentVariant | null {
  // Sort by priority (lower = higher priority)
  const sorted = [...variants].sort((a, b) => a.priority - b.priority);

  for (const variant of sorted) {
    if (evaluateRule(variant.rule, ctx)) {
      return variant;
    }
  }

  return null; // No match → use default props
}

/**
 * Apply a variant's overrides to component props.
 * Shallow merge: variant overrides replace top-level props.
 */
export function applyVariantOverrides(
  defaultProps: Record<string, unknown>,
  variant: ComponentVariant | null,
): Record<string, unknown> {
  if (!variant) return defaultProps;
  // Remove internal keys from overrides before merging
  const { ...overrides } = variant.overrides;
  return { ...defaultProps, ...overrides };
}

// ---------------------------------------------------------------------------
// Visitor Context Extraction
// ---------------------------------------------------------------------------

/**
 * Extract visitor context from HTTP request.
 * Used by page-serve Worker at serve time.
 */
export function extractVisitorContext(request: {
  url: string;
  headers: { get(name: string): string | null };
  cf?: { country?: string };
}): VisitorContext {
  const url = new URL(request.url);
  const params = url.searchParams;
  const ua = request.headers.get("user-agent") ?? "";
  const acceptLang = request.headers.get("accept-language") ?? "";
  const referrer = request.headers.get("referer") ?? "";
  const cookie = request.headers.get("cookie") ?? "";

  return {
    utmCampaign: params.get("utm_campaign") ?? undefined,
    utmSource: params.get("utm_source") ?? undefined,
    utmMedium: params.get("utm_medium") ?? undefined,
    referrer: referrer || undefined,
    referrerDomain: referrer ? extractDomain(referrer) : undefined,
    isReturningVisitor: cookie.includes("ease_rv=1"),
    device: detectDevice(ua),
    country: (request.cf?.country as string) ?? request.headers.get("cf-ipcountry") ?? undefined,
    language: parseAcceptLanguage(acceptLang),
    timeOfDay: getTimeOfDay(new Date()),
    contactStatus: cookie.includes("ease_auth=") ? "known" : "anonymous",
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractDomain(url: string): string | undefined {
  try { return new URL(url).hostname; }
  catch { return undefined; }
}

function detectDevice(ua: string): string {
  const lower = ua.toLowerCase();
  if (/tablet|ipad/i.test(lower)) return "tablet";
  if (/mobile|iphone|android(?!.*tablet)/i.test(lower)) return "mobile";
  return "desktop";
}

function parseAcceptLanguage(header: string): string | undefined {
  // Extract primary language tag (e.g., "en-US,en;q=0.9" → "en")
  const match = header.match(/^([a-zA-Z]{2})/);
  return match?.[1]?.toLowerCase();
}

export function getTimeOfDay(date: Date): string {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
}

/**
 * Create a badge label from a variant rule.
 */
export function formatBadge(rule: VariantRule): string {
  const emoji = RULE_BADGES[rule.type] ?? "🔧";
  return `${emoji} ${rule.value}`;
}
